<template>
  <div class="ac-overlay" @click.self="$emit('close')">
    <div class="ac-modal">
      <div class="ac-head">
        <div>
          <div class="ac-title">All comments</div>
          <div class="ac-subtitle">{{ openCount }} open · {{ resolvedCount }} resolved</div>
        </div>
        <button class="ac-close" title="Close" @click="$emit('close')">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div class="ac-filters">
        <button class="ac-filter" :class="{ active: filter === 'open' }" @click="filter = 'open'">Open</button>
        <button class="ac-filter" :class="{ active: filter === 'resolved' }" @click="filter = 'resolved'">Resolved</button>
        <button class="ac-filter" :class="{ active: filter === 'all' }" @click="filter = 'all'">All</button>
      </div>

      <div class="ac-body">
        <div v-if="!groups.length" class="ac-empty">
          {{ filter === 'open' ? 'No open comments.' : filter === 'resolved' ? 'No resolved comments.' : 'No comments yet on this journey.' }}
        </div>

        <div v-for="group in groups" :key="group.stageName" class="ac-group">
          <button class="ac-group-head" @click="$emit('select-stage', group.stageName)">{{ group.stageName }}</button>

          <div v-for="c in group.comments" :key="c.id" class="ac-item" :class="{ resolved: c.resolved }">
            <span class="ac-avatar" :style="{ background: avatarColor(c.author) }">{{ initials(c.author) }}</span>
            <div class="ac-item-body">
              <div class="ac-meta">
                <span class="ac-author">{{ c.author }}</span>
                <span class="ac-time">{{ relativeTime(c.createdAt) }}</span>
              </div>
              <div class="ac-text">{{ c.text }}</div>
              <button class="ac-resolve" :class="{ done: c.resolved }" @click="toggleResolved(c)">
                <span class="ac-resolve-dot"></span>
                {{ c.resolved ? 'Resolved' : 'Mark resolved' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { listAllComments, setCommentResolved } from './journeyComments'
import { initials, avatarColor, relativeTime } from './commentDisplay'

export default {
  name: 'all-comments-modal',
  props: {
    journeyKey: { type: String, default: '' }
  },
  data () {
    return {
      filter: 'open'
    }
  },
  computed: {
    comments () {
      return listAllComments(this.journeyKey)
    },
    openCount () {
      return this.comments.filter(c => !c.resolved).length
    },
    resolvedCount () {
      return this.comments.filter(c => c.resolved).length
    },
    groups () {
      const filtered = this.comments.filter(c => {
        if (this.filter === 'open') return !c.resolved
        if (this.filter === 'resolved') return c.resolved
        return true
      })
      const byStage = {}
      filtered.forEach(c => {
        if (!byStage[c.stageName]) byStage[c.stageName] = []
        byStage[c.stageName].push(c)
      })
      return Object.keys(byStage).map(stageName => {
        const comments = byStage[stageName].slice().sort((a, b) => a.createdAt - b.createdAt)
        return { stageName, comments, latest: comments[comments.length - 1].createdAt }
      }).sort((a, b) => b.latest - a.latest)
    }
  },
  methods: {
    toggleResolved (c) {
      setCommentResolved(this.journeyKey, c.id, !c.resolved)
    },
    initials,
    avatarColor,
    relativeTime
  }
}
</script>

<style scoped>
.ac-overlay{
  position:fixed;inset:0;background:rgba(10,18,32,0.5);z-index:2000;
  display:flex;align-items:center;justify-content:center;
}
.ac-modal{
  width:min(1100px,92vw);height:min(780px,86vh);background:#fff;border-radius:14px;
  box-shadow:0 30px 80px rgba(0,0,0,0.35);padding:22px 24px 20px;
  font-family:'Inter',system-ui,sans-serif;display:flex;flex-direction:column;
}
.ac-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}
.ac-title{font-size:17px;font-weight:700;color:#1a1d23}
.ac-subtitle{font-size:12.5px;color:#6b7280;margin-top:4px}
.ac-close{
  width:26px;height:26px;border-radius:6px;background:transparent;border:none;
  color:#9aa0ab;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;
}
.ac-close:hover{background:#f1f2f5;color:#1a1d23}

.ac-filters{display:flex;gap:6px;margin:16px 0 4px}
.ac-filter{
  padding:6px 13px;border-radius:999px;border:1px solid #dde0e6;background:#fff;color:#5d6c7e;
  font-size:12px;font-weight:600;cursor:pointer;font-family:inherit;
}
.ac-filter:hover{border-color:#0d9488}
.ac-filter.active{background:#16233a;border-color:#16233a;color:#fff}

.ac-body{flex:1;min-height:0;overflow-y:auto;margin-top:12px;padding-right:4px;display:flex;flex-direction:column}
.ac-empty{flex:1;display:flex;align-items:center;justify-content:center;font-size:13px;color:#94a2b1}

.ac-group{margin-bottom:18px}
.ac-group:last-child{margin-bottom:0}
.ac-group-head{
  display:block;font-family:'JetBrains Mono',monospace;font-size:11.5px;font-weight:700;color:#5d6c7e;
  background:none;border:none;padding:0;margin-bottom:9px;cursor:pointer;text-align:left;
}
.ac-group-head:hover{color:#0d9488;text-decoration:underline}

.ac-item{display:flex;gap:9px;padding:11px 0;border-top:1px solid #eef1f4}
.ac-group .ac-item:first-of-type{border-top:none;padding-top:0}
.ac-avatar{
  width:26px;height:26px;border-radius:50%;color:#fff;flex-shrink:0;
  display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;
}
.ac-item-body{flex:1;min-width:0}
.ac-meta{display:flex;align-items:baseline;gap:7px}
.ac-author{font-size:12.5px;font-weight:600;color:#16233a}
.ac-time{font-size:10.5px;color:#94a2b1}
.ac-text{font-size:12.5px;color:#16233a;line-height:1.5;margin-top:3px;word-break:break-word}
.ac-item.resolved .ac-text{color:#5d6c7e}

.ac-resolve{
  display:inline-flex;align-items:center;gap:5px;margin-top:6px;background:none;border:none;padding:0;
  font-size:11px;font-weight:600;color:#5d6c7e;cursor:pointer;font-family:inherit;
}
.ac-resolve:hover{color:#0d9488}
.ac-resolve-dot{width:10px;height:10px;border-radius:50%;border:1.5px solid currentColor;flex-shrink:0}
.ac-resolve.done{color:#0d9488}
.ac-resolve.done .ac-resolve-dot{background:#0d9488;border-color:#0d9488}
</style>



// WEBPACK FOOTER //
// AllCommentsModal.vue?11fa8180